<template>
  <div class="mb-3">
    <!-- Trip Details Section -->
    <div class="mb-6">
      <h3 v-if="type !== 'trip'" class="text-lg font-semibold mb-4">
        {{ typeConfig[type] }} informacija:
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="card bg-base-200">
          <div class="card-body">
            <h3 class="card-title text-lg">
              Bendra informacija
              <span
                v-if="tripData.status"
                :class="['badge', statusConfig[tripData.status].class]"
              >
                {{ statusConfig[tripData.status].text }}
              </span>
            </h3>
            <p>Data: {{ tripData.tripDate }}</p>
            <p v-if="tripData.basisType">
              Pagrindas: {{ basisTypeConfig[tripData.basisType] }}
            </p>
            <p>Organizuoja: {{ tripData.organization?.info }}</p>
            <p class="text-sm italic mt-2" v-if="tripData.notice">
              Pavėžėjimo pastaba: {{ tripData.notice }}
            </p>
            <div class="flex flex-wrap gap-2 mt-1">
              <div
                v-if="tripData.assistant_needed"
                class="badge badge-outline badge-info text-sm py-1"
              >
                Reikalingas palydėjimas
              </div>
              <div
                v-if="tripData.child_seat_needed"
                class="badge badge-outline badge-info text-sm py-1"
              >
                Reikalinga vaikiška kėdutė
              </div>
              <div
                v-if="tripData.wheelchair_needed"
                class="badge badge-outline badge-info text-sm py-1"
              >
                Reikalingas neįgaliojo vež.
              </div>
            </div>
          </div>
        </div>

        <div class="card bg-base-200">
          <div class="card-body">
            <h3 class="card-title text-lg">Automobilis ir vairuotojas</h3>

            <p>Automobilis: {{ tripData.selectedVehicleInfo }}</p>
            <p>Vairuotojas: {{ tripData.selectedDriverInfo }}</p>
            <p v-if="tripData.travelId">
              Kelionės nr.: {{ tripData.travelId }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Waypoints Timeline -->
    <div class="mb-6">
      <h3 class="text-lg font-semibold">{{ typeConfig[type] }} laikai:</h3>

      <ul class="timeline timeline-vertical">
        <li v-if="tripData.travelTimeStart">
          <div class="timeline-middle">
            <div class="rounded-full w-4 h-4 bg-neutral"></div>
          </div>
          <div class="timeline-end">
            <div class="font-semibold pt-8">
              {{ tripData.travelAddress }}
            </div>
            <div class="text-base">Laikas: {{ tripData.travelTimeStart }}</div>
            <div class="badge badge-outline">Pradžia</div>
          </div>
          <hr />
        </li>
        <li v-for="(waypoint, index) in tripData.tripWaypoints" :key="index">
          <hr />
          <div class="timeline-middle">
            <div
              class="rounded-full w-4 h-4"
              :class="waypoint.time_fact ? 'bg-success' : 'bg-error'"
            ></div>
          </div>
          <div class="timeline-end">
            <div class="text-lg font-semibold pt-8">{{ waypoint.address }}</div>
            <div class="text-base">Laikas: {{ waypoint.time }}</div>
            <div v-if="waypoint.time_fact" class="text-base">
              Faktinis laikas: {{ waypoint.time_fact }}
            </div>

            <div v-if="waypoint.isInRestriction" class="badge badge-warning">
              "Patenka į buferį"
            </div>
          </div>
          <hr />
        </li>
        <li v-if="tripData.travelTimeFinish">
          <hr />
          <div class="timeline-middle">
            <div class="rounded-full w-4 h-4 bg-neutral"></div>
          </div>
          <div class="timeline-end">
            <div class="text-lg font-semibold pt-8">
              {{ tripData.travelAddress }}
            </div>
            <div class="text-base">Laikas: {{ tripData.travelTimeFinish }}</div>

            <div class="badge badge-outline">Pabaiga</div>
          </div>
        </li>
      </ul>
    </div>

    <!-- Passengers Section -->
    <div class="mb-6">
      <h3 class="text-lg font-semibold mb-4">Keleiviai:</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(passenger, index) in tripData.passengers"
          :key="index"
          class="card bg-base-200 shadow-xs"
        >
          <div class="card-body">
            <h3 class="card-title text-lg">
              {{ passenger.firstName }} {{ passenger.lastName }}
              <div
                v-if="passenger.service_restricted"
                class="badge badge-sm badge-error font-normal"
              >
                Apribotos paslaugos
              </div>
            </h3>
            <div class="space-y-1">
              <p>Tel. nr.: {{ passenger.phone }}</p>
              <p>El. paštas: {{ passenger.email }}</p>
              <p v-if="passenger.service_type">
                Paslaugų teikimo pagrindas:
                {{
                  referenceDataStore.clientServiceTypeConfig[
                    passenger.service_type
                  ]?.text
                }}
              </p>
              <p v-if="passenger.service_restricted">
                Apribotas paslaugų teikimas iki:
                {{ passenger.service_restriction_until }}
              </p>
              <p v-if="passenger.assistantPersonCount !== undefined">
                Lydinčių asm.: {{ passenger.assistantPersonCount }}
              </p>

              <div class="flex flex-wrap gap-2 mt-1">
                <div
                  v-if="passenger.disabled"
                  class="badge badge-sm badge-outline badge-info text-sm py-1"
                >
                  Neįgaliojo vež.
                </div>
                <div
                  v-if="passenger.assistantPersons"
                  class="badge badge-sm badge-outline badge-info text-sm py-1"
                >
                  Lydintis asmuo
                </div>
              </div>
              <p v-if="passenger.description" class="text-sm italic mt-2">
                Pastaba: {{ passenger.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div
        class="mt-6"
        v-if="
          tripData.assistant_persons_count !== undefined &&
          tripData.assistant_persons_count !== null
        "
      >
        <h3 class="text-lg font-semibold mb-4">
          Lydinčių keleivių skaičius:
          <span class="font-normal">
            {{ tripData.assistant_persons_count }}</span
          >
        </h3>
      </div>
    </div>
    <div v-if="tripData.driver_notice">
      <h3 class="text-lg font-semibold mb-4">Vairuotojo žinutė:</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="card bg-base-200 shadow-xs">
          <div class="card-body">{{ tripData.driver_notice }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, onMounted } from "vue";
import { useReferenceDataStore } from "@/stores/referenceData";

const referenceDataStore = useReferenceDataStore();

onMounted(() => {
  referenceDataStore.ensureDataLoaded();
});

defineProps({
  tripData: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    default: "travel",
  },
});

/*
  Configs
*/
const basisTypeConfig = {
  tripBasisType1: "SPIS",
  tripBasisType2: "Hemodializė",
  tripBasisType3: "Virš 2 km. iki stotelės",
  tripBasisType4: "Gulintis",
  tripBasisType5: "Kita",
};

const statusConfig = {
  tripStatus1: {
    text: "Suplanuotas",
    class: "badge-success",
  },
  tripStatus2: {
    text: "Įvykdytas",
    class: "badge-info",
  },
  tripStatus3: {
    text: "Atšauktas",
    class: "badge-error",
  },
  tripStatus4: {
    text: "Vykdoma",
    class: "badge-warning",
  },
  tripStatus5: {
    text: "Neatvyko",
    class: "badge-error",
  },
  tripStatus6: {
    text: "Nepatvirtintas",
    class: "badge-error",
  },
};

const typeConfig = {
  travel: "Kelionės",
  trip: "Pavėžėjimo",
};
</script>
